import React, { useEffect, useRef, useState } from 'react';
import { Form, Field } from 'react-final-form';
import {
    makeStyles,
    Theme,
    createStyles,
    Button,
    DialogActions,
    Grid,
    IconButton,
    Box,
    Card,
    Typography,
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { AgGridReact } from '@ag-grid-community/react';
import {
    AllModules,
    ColDef,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
} from '@ag-grid-enterprise/all-modules';
import { AgGridStyle, AgGridTableWrapper } from '../../components/AgGridStyle';
import DeleteIcon from '@material-ui/icons/Delete';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import { mainTheme } from '../../Theme';
import { Role, RoleQueryParams, RoleSelector, RolesQuery, RolesQueryData } from '../selectors/RoleSelector';
import { Brand, BrandSelector, BrandsQuery, BrandsQueryData } from '../selectors/BrandSelector';
import { PartyWebUser, PartyWebUserSelector, PartyWebUserSelectorParams } from '../selectors/PartyWebUserSelector';
import { FormApi } from 'final-form';
import { Alert } from '@material-ui/lab';
import gql from 'graphql-tag';
import { useQuery, useSubscription } from 'react-apollo';
import { RvLoader } from '../../components/Loader';
import { client } from '../..';
import { showNotification } from '../../App';
import DialogBox from '../../Dashboard/Component/DialogBox';
import { DialogTitleExtension } from '../common/DialogTitleExtension';
import { isEqual } from 'lodash';

interface FormValues {
    contact: Partial<PartyWebUser> | null;
    role: Partial<Role> | null;
    brand: Partial<Brand> | null;
    isSaveAndClose?: boolean;
}

interface PublishMatterDialogProps extends Pick<PartyWebUserSelectorParams, 'matterId'>{
    onClose?: () => void;
    matterGuid: string;
    client: string;
    matterTitle: string;
    fileNumber: string;
}

interface PublishMatterDialogState {
    deleteCandidate?: PublishedMatterWebUser | null;
    showDiscard?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
        },
        dialogTitle: {
            color: mainTheme.TemplateColor.Primary,
        },
        dialogPaper: {
            minWidth: 800,
            height: 640,
        },
        dialogContent: {
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: theme.spacing(2, 3),
            gap: theme.spacing(2),
        },
        formCard: {
            padding: theme.spacing(2, 2.5),
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            flexShrink: 0,
        },
        gridCard: {
            flex: 1,
            position: 'relative',
            minHeight: 0,
            borderRadius: 8,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
        },
        sectionHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1.25, 2),
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: mainTheme.NeutralColors.LightBlue2,
        },
        sectionHeaderIcon: {
            color: mainTheme.TemplateColor.Primary,
            fontSize: 20,
        },
        sectionHeaderText: {
            fontWeight: 600,
            fontSize: 13,
            color: mainTheme.BrandColors.TextPrimary,
            letterSpacing: 0.3,
        },
        gridWrapper: {
            position: 'absolute',
            top: 40,
            left: 0,
            right: 0,
            bottom: 0,
        },
        dialogActions: {
            padding: theme.spacing(1.5, 3),
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
        },
    })
);

// tslint:disable-next-line: no-any
const ActionCellRenderer: React.FC<ICellRendererParams> = (params) => {
    const onDeleteRow: (row: PublishedMatterWebUser) => void = params.context.onDeleteRow;
    return (
        <IconButton size="small" onClick={() => onDeleteRow(params.data)}>
            <DeleteIcon fontSize="small" />
        </IconButton>
    );
};

// tslint:disable-next-line: no-any
export const PublishMatterDialog: React.FC<PublishMatterDialogProps> = (props) => {
    const classes = useStyles();

    const gridApiRef = useRef<GridApi | null>(null);
    
    const isFormChangedRef = useRef(false);
    
    const [state, setState] = useState<PublishMatterDialogState>({
        deleteCandidate: null,
        showDiscard: false
    });
    
    const brandQuery = useQuery<BrandsQueryData>(BrandsQuery, {
        fetchPolicy: 'cache-first',
    });
    
    const roleQuery = useQuery<RolesQueryData, RoleQueryParams>(RolesQuery, {
        variables: {
            isExternal: true,
        },
        fetchPolicy: 'cache-first',
    });

    const publishMatterQuery = useQuery<PublishedMatterWebUsersData, PublishMatterWebUserParameters>(PublishedMatterWebUsersQuery, {
        variables: {
            offset: 0,
            first: 100,
            matterId: props.matterId,
        },
    });
    
    const removePublishedMatterAccessNotification = useSubscription<RemovePublishedMatterAccessNotificationData>(RemovePublishedMatterAccessNotification);
    
    const publishMatterNotification = useSubscription<PublishMatterNotificationData>(PublishMatterNotification);
    
    useEffect(() => {
        
        if (removePublishedMatterAccessNotification.data) {
            publishMatterQuery.refetch();
            if (removePublishedMatterAccessNotification.data && removePublishedMatterAccessNotification.data.removePublishedMatterAccessNotification.status) {
                showNotification('Success', removePublishedMatterAccessNotification.data.removePublishedMatterAccessNotification.message, 'info');
            } else {
                showNotification('Failed', removePublishedMatterAccessNotification.data?.removePublishedMatterAccessNotification.message || 'Failed to remove access', 'error');
            }
        }
        
    }, [removePublishedMatterAccessNotification.data]);
    
    useEffect(() => {
        
        if (publishMatterNotification.data) {
            publishMatterQuery.refetch();
            if (publishMatterNotification.data && publishMatterNotification.data.publishMatterNotification.status) {
                showNotification('Success', publishMatterNotification.data.publishMatterNotification.message, 'info');
            } else {
                showNotification('Failed', publishMatterNotification.data?.publishMatterNotification.message || 'Failed to publish matter', 'error');
            }
        }
        
    }, [publishMatterNotification.data]);

    const columnDefs: ColDef[] = [
        { headerName: 'Name', field: 'name', flex: 1, sortable: true },
        { headerName: 'Email', field: 'email', flex: 1, sortable: true },
        { headerName: 'Role', field: 'role', flex: 1, sortable: true },
        {
            headerName: 'Action',
            field: 'action',
            width: 80,
            sortable: false,
            suppressMenu: true,
            cellRendererFramework: ActionCellRenderer,
        },
    ];

    const handleGridReady = (event: GridReadyEvent) => {
        gridApiRef.current = event.api;
    };
    
    const isHasAccount = (values: FormValues) => {
        // if the contact has userId then the user has account else not
        return values.contact?.userId && values.contact.userId.length > 0;
    };

    const onSubmit = (values: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {    
        client.mutate<PublishMatterData, PublishMatterParameters>({
            mutation: PublishMatterMutation,
            variables: {
                matterGuid: props.matterGuid,
                partyGuid: values.contact?.nameGuid || '', // Assuming nameGuid is used as partyGuid
                name: values.contact?.name || '',
                emailAddress: values.contact?.email || '',
                ...(!isHasAccount(values) ? {
                    roleId: values.role?.roleIdString,
                    roleName: values.role?.rolename,
                    brand: values.brand?.id,
                } : {
                    roleId: undefined,
                    roleName: undefined,
                    brand: undefined
                }),
            },
        }).then(response => {
            
            showNotification(null, 'Publish successfully submitted', 'info');
                        
            if (values.isSaveAndClose && props.onClose) {
                props.onClose();
            }
            
            form.setConfig('keepDirtyOnReinitialize', false);
            
            form.reset({
                contact: null,
                // if there is only 1 role then use that as default value for role selector, else set to null to force user to select a role
                role: roleQuery.data?.roleList.role.length === 1 ? roleQuery.data?.roleList.role[0] : null,
                brand: brandQuery.data?.administrator.brands.brand[0] || null,
                isSaveAndClose: false,
            });
            
            const registeredPublishFields = form.getRegisteredFields();
            
            if (registeredPublishFields.includes('contact')) {
                form.resetFieldState('contact');
            }
            if (registeredPublishFields.includes('role')) {
                form.resetFieldState('role');
            }
            if (registeredPublishFields.includes('brand')) {
                form.resetFieldState('brand');
            }
            
            form.setConfig('keepDirtyOnReinitialize', true);
            
        }).catch(error => {
            showNotification(null, error, 'error');
        });
    };

    const handleDeleteRow = (row: PublishedMatterWebUser) => {
        setState((prevState) => {
            return {
                ...prevState,
                deleteCandidate: row,
            };
        });
    };
    
    const onCloseDiscardChanges = (agree: boolean) => {
        if (agree) {
            if (props.onClose) {
                props.onClose();
            }
        }
        setState((prevState) => {
            return {
                ...prevState,
                showDiscard: false,
            };
        });
    };
    
    const onUserDiscardChanges = (agree: boolean) => {
        if (agree && state.deleteCandidate) {
            
            client.mutate<RemovePublishedMatterAccessData, RemovePublishedMatterAccessParameters>({
                mutation: RemovePublishedMatterAccessMutation,
                variables: {
                    matterGuid: props.matterGuid,
                    partyGuid: state.deleteCandidate.nameGuid || '', // Assuming nameGuid is used as partyGuid
                },
            }).then(response => {
                showNotification(null, 'Remove successfully submitted', 'info');
                setState((prevState) => {
                    return {
                        ...prevState,
                        deleteCandidate: null,
                    };
                });
            }).catch(error => {
                showNotification(null, error, 'error');
                setState((prevState) => {
                    return {
                        ...prevState,
                        deleteCandidate: null,
                    };
                });
            });
            
        } else {
            setState((prevState) => {
                return {
                    ...prevState,
                    deleteCandidate: null,
                };
            });
        }
    };

    const onClose = () => {
        
        if (isFormChangedRef.current) {
            setState((prevState) => {
                return {
                    ...prevState,
                    showDiscard: true,
                }
            })
        } else {
            if (props.onClose) {
                props.onClose();
            }
        }
    };
    
    const getContactStatus = (values: FormValues) => {
        if (!values.contact) {
            return null;
        }
        
        console.log('userId', values.contact.userId);
        
        if (!isHasAccount(values)) {
            return (
                <Grid item={true} lg={12} xs={12}>
                    <Alert
                        severity="warning"
                    >
                        This contact doest not have a user account. A new user will be created and a registration email will be sent.
                    </Alert>
                </Grid>
            );
        } else {
            return (
                <Grid item={true} lg={12} xs={12}>
                    <Alert 
                        severity="warning"
                    >
                        This contact already has a user account. Selecting save will link them to this matter.
                    </Alert>
                </Grid>
            );
        }
    };
    
    const getAdditionalFeildsForContact = (values: FormValues) => {
        if (!values.contact) {
            return null;
        }
        if (!isHasAccount(values)) {
            return (
                <>
                    <Grid item={true} lg={12} xs={12}>
                        <Grid container={true} spacing={2}>
                            <Grid item={true} lg={6} xs={12}>
                                <Field
                                    name="role"
                                    validate={(value) => (!value && !isHasAccount(values) ? 'Role is required' : undefined)}
                                    render={({ input, meta }) => (
                                        <RoleSelector 
                                            {...input}
                                            label="Role"
                                            variant="standard"
                                            fullWidth={true}
                                            required={true}
                                            error={meta.touched && !!meta.error}
                                            size="small"
                                            onChange={(_, value) => input.onChange(value)}
                                            helperText={meta.touched && meta.error}
                                            isExternal={true}
                                            // multiple={true}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item={true} lg={6} xs={12}>
                                <Field
                                    name="brand"
                                    validate={(value) => (!value && !isHasAccount(values) ? 'Brand is required' : undefined)}
                                    render={({ input, meta }) => (
                                        <BrandSelector
                                            {...input}
                                            label="Brand"
                                            variant="standard"
                                            fullWidth={true}
                                            required={true}
                                            error={meta.touched && !!meta.error}
                                            size="small"
                                            onChange={(_, value) => input.onChange(value)}
                                            helperText={meta.touched && meta.error}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </>
            );
        }
        
        return null;
    }
    
    const getInitialize = () => {
        if (brandQuery.loading || roleQuery.loading) {
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                >
                    <RvLoader />
                </Box>
            )
        } else {
            return (
                <Form<FormValues>
                    onSubmit={onSubmit}
                    keepDirtyOnReinitialize={true}
                    initialValues={{
                        contact: null,
                        // if there is only 1 role then use that as default value for role selector, else set to null to force user to select a role
                        role: roleQuery.data?.roleList.role.length === 1 ? roleQuery.data?.roleList.role[0] : null,
                        brand: brandQuery.data?.administrator.brands.brand[0] || null,
                        isSaveAndClose: false,
                    }}
                    subscription={{ values: true, submitting: true, pristine: true, errors: true, initialValues: true, }}
                    render={({ form, handleSubmit, values, submitting, pristine, errors, initialValues }) => {
                        
                        // update state pristine when values change
                        const isFormChanged = isEqual(values, initialValues);
                        
                        isFormChangedRef.current = !isFormChanged;
                        
                        return (
                            <>
                                <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
                                    <DialogTitleExtension 
                                        title="Publish Matter"
                                        subTitle={`${props.client}${props.fileNumber ? ` (${props.fileNumber})` : ''}`}
                                        description={props.matterTitle}
                                    />
                                </DialogTitle>
                                <DialogContent dividers={true} className={classes.dialogContent}>
                                    <Box component="form" onSubmit={handleSubmit} className={classes.formCard}>
                                        <Grid container={true} spacing={2}>
                                            <Grid item={true} lg={12} xs={12}>
                                                <Grid container={true} spacing={2}>
                                                    <Grid item={true} lg={6} xs={12}>
                                                        <Field
                                                            name="contact"
                                                            validate={(value) => (!value ? 'Contact is required' : undefined)}
                                                            render={({ input, meta }) => (
                                                                <PartyWebUserSelector
                                                                    {...input}
                                                                    label="Contact"
                                                                    variant="standard"
                                                                    fullWidth={true}
                                                                    required={true}
                                                                    error={meta.touched && !!meta.error}
                                                                    size="small"
                                                                    onChange={(_, value) => {
                                                                        input.onChange(value);
                                                                        form.change('role', roleQuery.data && roleQuery.data?.roleList.role.length === 1 ? roleQuery.data?.roleList.role[0] : null);
                                                                        form.change('brand', brandQuery.data?.administrator.brands.brand[0] || null);
                                                                    }}
                                                                    helperText={meta.touched && meta.error}
                                                                    matterId={props.matterId}
                                                                    isExcludeUsedParty={true}
                                                                    // multiple={true}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            {getContactStatus(values)}
                                            {getAdditionalFeildsForContact(values)}
                                        </Grid>
                                    </Box>
                                    {/* Grid Section */}
                                    <Card className={classes.gridCard} elevation={0}>
                                        <Box className={classes.sectionHeader}>
                                            <PeopleOutlinedIcon className={classes.sectionHeaderIcon} />
                                            <Typography className={classes.sectionHeaderText}>
                                                User Access
                                            </Typography>
                                        </Box>
                                        <Box className={classes.gridWrapper}>
                                            <AgGridStyle className="ag-theme-material">
                                                <AgGridTableWrapper className="grid-container">
                                                    <AgGridReact
                                                        modules={AllModules}
                                                        columnDefs={columnDefs}
                                                        rowData={publishMatterQuery.data?.publishedMatterWebUsers.publishedMatterWebUser || []}
                                                        context={{ onDeleteRow: handleDeleteRow }}
                                                        onGridReady={handleGridReady}
                                                        gridOptions={{
                                                            defaultColDef: {
                                                                resizable: true,
                                                            },
                                                        }}
                                                    />
                                                </AgGridTableWrapper>
                                            </AgGridStyle>
                                        </Box>
                                    </Card>
                                </DialogContent>
                                <DialogActions className={classes.dialogActions}>
                                    <Button
                                        color="primary"
                                        onClick={() => {
                                            form.change('isSaveAndClose', true);
                                            form.submit();
                                        }}
                                        disabled={submitting || pristine}
                                        size="large"
                                    >
                                        Save & Close
                                    </Button>
                                    <Button
                                        color="primary"
                                        onClick={() => {
                                            form.change('isSaveAndClose', false);
                                            form.submit();
                                        }}
                                        disabled={submitting || pristine}
                                        size="large"
                                    >
                                        Save
                                    </Button>
                                    <Button 
                                        onClick={onClose} 
                                        color="primary"
                                    >
                                        Cancel
                                    </Button>
                                </DialogActions>
                            </>
                        );
                    }} 
                />
            )
        }
    }

    return (
        <>
        
            <DialogBox
                title="Discard Changes"
                content="Are you sure you want to close the form?"
                show={state.showDiscard}
                isAgree={onCloseDiscardChanges}
                disAgreeLabel="No"
                agreeLabel="Yes"
            />
        
            <Dialog
                open={true}
                fullWidth={true}
                maxWidth="lg"
                onClose={onClose}
                className={classes.root}
                classes={{
                    paper: classes.dialogPaper,
                }}
            >
                {getInitialize()}
            </Dialog>

            <DialogBox
                title="User Access"
                // tslint:disable-next-line:max-line-length
                content={`Are you sure you want to remove ${state.deleteCandidate?.name || 'this user'} from having access to this matter? They will no longer be able to view or edit the matter.`}
                show={!!state.deleteCandidate}
                isAgree={onUserDiscardChanges}
                disAgreeLabel={'No'}
                agreeLabel={'Yes'}
            />
        </>
    );
};

interface PublishMatterWebUserParameters {
    offset: number;
    first?: number;
    filter?: string;
    matterId: number;
}

interface PublishedMatterWebUser {
    id: number;
    userId: string;
    name: string;
    email: string;
    role: string;
    nameGuid: string;
}

interface PublishedMatterWebUsersData {
    publishedMatterWebUsers: {
        recordCount: number;
        publishedMatterWebUser: PublishedMatterWebUser[];
    }
}

const PublishedMatterWebUsersQuery = gql`
    query PublishedMatterWebUsers(
        $offset: Int!
        $first: Int
        $filter: String
        $matterId: Int!
    ) {
        publishedMatterWebUsers(
            offset: $offset
            first: $first
            filter: $filter
            matterId: $matterId
        ) {
            recordCount
            publishedMatterWebUser {
                id
                userId
                name
                email
                role
                nameGuid
            }
        }
    }
`;


interface PublishMatterParameters {
    matterGuid: string;
    partyGuid: string;
    name: string;
    emailAddress: string;
    roleId?: string;
    roleName?: string;
    brand?: number;
}

interface PublishMatterData {
    matter: {
        publishMatter: {
            publish: {
                status: string;
                error: string | null;
            }
        }
    }
}

const PublishMatterMutation = gql`
    mutation PublishMatter(
        $matterGuid: String!
        $partyGuid: String!
        $name: String!
        $emailAddress: String!
        $roleId: String
        $roleName: String
        $brand: Int
    ) {
        matter {
            publishMatter {
                publish(
                    matterGuid: $matterGuid
                    partyGuid: $partyGuid
                    name: $name
                    emailAddress: $emailAddress
                    roleId: $roleId
                    roleName: $roleName
                    brand: $brand
                ) {
                    status
                    error
                }
            }
        }
    }
`;


interface RemovePublishedMatterAccessParameters {
    matterGuid: string;
    partyGuid: string;
}

interface RemovePublishedMatterAccessData {
    matter: {
        publishMatter: {
            removePublishedMatterAccess: {
                status: string;
                error: string | null;
            }
        }
    }
}

const RemovePublishedMatterAccessMutation = gql`
    mutation RemovePublishedMatterAccess(
        $matterGuid: String!
        $partyGuid: String!
    ) {
        matter {
            publishMatter {
                removePublishedMatterAccess(
                    matterGuid: $matterGuid
                    partyGuid: $partyGuid
                ) {
                    status
                    error
                }
            }
        }
    }
`;

interface RemovePublishedMatterAccessNotificationData {
    removePublishedMatterAccessNotification: {
        id: number;
        message: string;
        status: string;
        description: string;
    }
}

const RemovePublishedMatterAccessNotification = gql`
    subscription RemovePublishedMatterAccessNotification{
        removePublishedMatterAccessNotification{
            id
            message
            status
            description
        }
    }
`;

interface PublishMatterNotificationData {
    publishMatterNotification: {
        id: number;
        message: string;
        status: string;
        description: string;
    }
}

const PublishMatterNotification = gql`
    subscription PublishWebUser{
		publishMatterNotification{
			id
				message
				status
				description
		}
	}
`;